/**
 * VÉRAULT Portal — Client: Documents
 * Read-only editorial list of every document the advisor has shared.
 */
import React from "react";
import { motion } from "framer-motion";
import { FileText, ExternalLink, Download, ScrollText, CheckCircle2 } from "lucide-react";
import {
  Surface,
  PageHeader,
  EditorialRule,
  StatusTag,
  Empty,
  Skeleton,
  api,
  fmt,
  motion as portalMotion,
} from "../../index";

const { fadeInUp, stagger } = portalMotion;

function AgreementRow({ a }) {
  const acked = a.status === "acknowledged" || a.status === "signed";
  return (
    <li>
      <div className="flex items-center gap-4 px-5 sm:px-6 py-4" data-testid={`agreement-${a.id}`}>
        <ScrollText className="size-4 text-[hsl(var(--accent-gold))] shrink-0" strokeWidth={1.5} />
        <div className="flex-1 min-w-0">
          <p className="font-serif text-base text-[hsl(var(--foreground))] truncate">
            {a.title || a.type}
          </p>
          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
            {[a.trip_name, acked ? `Acknowledged ${a.acknowledged_at ? fmt.formatDate(a.acknowledged_at) : ""}` : a.status].filter(Boolean).join(" · ")}
          </p>
        </div>
        {acked && (
          <>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/client-portal/agreements/${a.id}/pdf`}
              className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] border border-[rgba(0,0,0,0.06)] hover:border-[hsl(var(--accent-gold))]/30 transition-colors"
              data-testid={`agreement-pdf-${a.id}`}
              title="Download PDF copy"
            >
              <Download className="size-2.5" strokeWidth={1.6} />
              PDF
            </a>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-[0.16em] bg-[hsl(var(--accent-sage,170_18%_42%))]/15 text-[hsl(var(--accent-sage,170_18%_42%))]">
              <CheckCircle2 className="size-2.5" strokeWidth={2} />
              Signed
            </span>
          </>
        )}
      </div>
    </li>
  );
}

export default function ClientDocuments() {
  const [docs, setDocs] = React.useState(null);
  const [agreements, setAgreements] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    Promise.allSettled([
      api.get("/client-portal/documents"),
      api.get("/client-portal/agreements"),
    ]).then(([rd, ra]) => {
      if (!alive) return;
      if (rd.status === "fulfilled") setDocs(Array.isArray(rd.value.data) ? rd.value.data : []);
      else { setDocs([]); setError("Couldn't load your documents."); }
      if (ra.status === "fulfilled") setAgreements(Array.isArray(ra.value.data) ? ra.value.data : []);
      else setAgreements([]);
    });
    return () => {
      alive = false;
    };
  }, []);

  const ackedAgreements = (agreements || []).filter((a) => a.status === "acknowledged" || a.status === "signed");

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-10">
      <motion.div variants={fadeInUp}>
        <PageHeader
          eyebrow="Documents"
          title="Everything in one place."
          lede="Confirmations, vouchers, itineraries, and the agreements you've signed."
        />
      </motion.div>

      {/* Signed agreements section */}
      {ackedAgreements.length > 0 && (
        <motion.div variants={fadeInUp} data-testid="client-documents-agreements">
          <EditorialRule label={`Agreements · ${ackedAgreements.length} signed`} />
          <div className="mt-5">
            <Surface variant="default" padded={false} className="overflow-hidden">
              <ul className="divide-y divide-[rgba(0,0,0,0.05)]">
                {ackedAgreements.map((a) => <AgreementRow key={a.id} a={a} />)}
              </ul>
            </Surface>
          </div>
        </motion.div>
      )}

      <motion.div variants={fadeInUp}>
        <EditorialRule label={docs ? `Trip documents · ${docs.length}` : "loading"} />
        <div className="mt-5">
          {error ? (
            <Empty title={error} icon={<FileText className="size-6" strokeWidth={1.25} />} />
          ) : !docs ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16" />)}
            </div>
          ) : docs.length === 0 ? (
            <Empty
              title="No trip documents yet"
              hint="Confirmations and vouchers your advisor shares will appear here."
              icon={<FileText className="size-6" strokeWidth={1.25} />}
            />
          ) : (
            <Surface variant="default" padded={false} className="overflow-hidden">
              <ul className="divide-y divide-[rgba(0,0,0,0.05)]">
                {docs.map((d) => (
                  <li key={d.id}>
                    <div className="flex items-center gap-4 px-5 sm:px-6 py-4" data-testid={`document-${d.id}`}>
                      <FileText className="size-4 text-[hsl(var(--accent-gold))] shrink-0" strokeWidth={1.5} />
                      <div className="flex-1 min-w-0">
                        <p className="font-serif text-base text-[hsl(var(--foreground))] truncate">
                          {d.title || d.filename || "Document"}
                        </p>
                        <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                          {[d.trip_name, d.document_type, d.uploaded_at ? fmt.formatDate(d.uploaded_at) : null].filter(Boolean).join(" · ")}
                        </p>
                      </div>
                      {d.url && (
                        <a
                          href={d.url}
                          target="_blank"
                          rel="noreferrer"
                          className="size-9 inline-flex items-center justify-center rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] hover:bg-[hsl(var(--background-paper))]"
                          title="Open"
                        >
                          <ExternalLink className="size-3.5" strokeWidth={1.5} />
                        </a>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Surface>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
